import type {
	PromoCampaignErrorRow,
	PromoCampaignImportFileProps,
} from '../model/types'

import { sum } from 'lodash/fp'
import { DownloadIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	Dropzone,
	DropzoneInput,
	DropzoneUploadIcon,
	DropzoneZone,
} from '@/shared/components/ui/dropzone'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { LinkButton } from '@/shared/components/ui/link-button'
import { Loader } from '@/shared/components/ui/loader'

import { buildDownloadPromoCampaignTemplateHandler } from '../lib/buildDownloadPromoCampaignTemplateHandler'
import { buildPromoCampaignErrorFileHandler } from '../lib/buildPromoCampaignErrorFileHandler'
import { getCountPromoCampaignErrors } from '../lib/getCountPromoCampaignErrors'
import { useFileDropped } from '../lib/useFileDropped'

const ErrorsPanel = ({
	errors,
}: {
	errors: Record<string, PromoCampaignErrorRow[]>
}) => {
	const { t } = useTranslation()

	const handleDownloadErrorsFile = buildPromoCampaignErrorFileHandler(t)
	const countErrors = getCountPromoCampaignErrors(errors)
	const totalCountErrors = sum(countErrors.map(([, val]) => val))

	return (
		<Alert variant='destructive'>
			<AlertTitle>
				{totalCountErrors > 0
					? t('import_errors_panel', { count: totalCountErrors })
					: t('fatal_error_title')}
			</AlertTitle>
			<AlertDescription>
				<div className='flex flex-col gap-2'>
					<ul className='list-inside list-disc'>
						{countErrors
							.filter(([, val]) => val > 0)
							.map(([key, val]) => (
								<li key={key}>
									{t(`import_error_${key}`)}
									{`(${val})`}
								</li>
							))}
					</ul>
					{totalCountErrors > 0 && (
						<LinkButton
							onClick={() => handleDownloadErrorsFile(errors)}
							variant='brand'
						>
							<DownloadIcon />
							{t('download_file_with_import_errors')}
						</LinkButton>
					)}
				</div>
			</AlertDescription>
		</Alert>
	)
}
export default function PromoImportFile(props: PromoCampaignImportFileProps) {
	const { t } = useTranslation()

	const handleDownloadTemplate = buildDownloadPromoCampaignTemplateHandler()
	const { handleFileDropped, isLoading, errors } = useFileDropped(props)

	return (
		<div className='relative flex h-full items-center justify-center'>
			<div className='w-[500px]'>
				<h3 className='mb-2 text-sm font-bold'>
					{t('promo_upload_file_title')}
				</h3>
				<Blanket
					className='bg-backdrop blanket-abs blanket-products'
					isTinted={isLoading}
				>
					<Loader />
				</Blanket>
				<div className='mb-5'>
					<Dropzone
						accept={{
							'text/csv': ['.csv'],
							'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
								['.xls', '.xlsx'],
							'application/vnd.ms-excel': ['.xls', '.xlsx'],
						}}
						onDropAccepted={handleFileDropped}
					>
						<div className='grid gap-3'>
							<DropzoneZone>
								<DropzoneInput />
								<Empty className='p-4!'>
									<EmptyHeader>
										<EmptyMedia variant='icon'>
											<DropzoneUploadIcon />
										</EmptyMedia>
										<EmptyTitle>
											{t('dropzone.title', {
												defaultValue: 'Drop files here or click to upload',
											})}
										</EmptyTitle>
										<EmptyDescription>
											{t('dropzone.description', {
												defaultValue:
													'You can upload files up to 10MB in size. Supported formats: CSV, XLS, XLSX.',
											})}
										</EmptyDescription>
									</EmptyHeader>
								</Empty>
							</DropzoneZone>
						</div>
					</Dropzone>
				</div>
				{errors ? (
					<ErrorsPanel errors={errors} />
				) : (
					<Card className='border'>
						<CardHeader>
							<CardTitle>{t('modal_import_guide')}</CardTitle>
						</CardHeader>
						<CardContent>
							<ul className='list-decimal space-y-2 pl-5'>
								<li>
									{t('promo_import_step_1')}
									<LinkButton
										variant='brand'
										onClick={handleDownloadTemplate}
										className='ml-1'
									>
										{t('promo_import_types')}
									</LinkButton>
								</li>
								<li>{t('promo_import_step_2')}</li>
								<li>{t('promo_import_step_3')}</li>
							</ul>
							<p className='text-sm font-bold'>{t('promo_import_hint')}</p>
						</CardContent>
					</Card>
				)}
			</div>
		</div>
	)
}
